/**
 * Core photo helpers for gallery + wet/dry heuristics.
 *
 * Payload reference (GET /api/core_photos, include_renditions=true, include_sas=true):
 * - Top-level sas_url: original blob (read SAS)
 * - renditions: object keyed by rendition_kind → { blob_path, sas_url?, width_px, ... }
 *   Typical kinds mentioned in backend docs: thumb_small, preview-ish keys (vary by pipeline).
 *
 * Inspect real rows to tune inferCondition tokens (parent_folder_path + file_name).
 */

export const CORE_PHOTO_RENDITION_THUMB_KEYS = ['thumb_small', 'thumbnail', 'preview_small'];

export const CORE_PHOTO_RENDITION_PREVIEW_KEYS = ['preview', 'preview_medium', 'preview_large'];

/** @typedef {'wet' | 'dry' | 'unknown'} CorePhotoCondition */

/**
 * Rough token match on folder + filename. Adjust arrays after inspecting production paths.
 *
 * @param {Record<string, unknown>} photo — image_asset row from API
 * @returns {CorePhotoCondition}
 */
export function inferCondition(photo) {
  const path = `${photo.parent_folder_path || ''}/${photo.file_name || ''}`.toLowerCase();

  /** Stronger indicators first (substring checks are coarse). */
  const dryMarkers = ['/dry/', '/dry-', '_dry_', '-dry-', ' dry ', 'dry box', 'drycore'];
  const wetMarkers = ['/wet/', '/wet-', '_wet_', '-wet-', ' wet ', 'wetted', 'wet box', 'wetcore'];

  const hasDry = dryMarkers.some((m) => path.includes(m));
  const hasWet = wetMarkers.some((m) => path.includes(m));
  if (hasDry && hasWet) return 'unknown';
  if (hasDry) return 'dry';
  if (hasWet) return 'wet';
  return 'unknown';
}

function roundDepth(d) {
  if (d == null || d === '') return null;
  const n = Number(d);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 1000) / 1000;
}

/** Stable key so wet/dry pairs at the same interval group together */
export function intervalKeyFromPhoto(photo) {
  const from = roundDepth(photo.depth_from_m);
  const to = roundDepth(photo.depth_to_m);
  const box =
    photo.box_start != null || photo.box_end != null
      ? `${photo.box_start ?? ''}-${photo.box_end ?? ''}`
      : '';
  return `${photo.hole_id || ''}|${from ?? 'na'}|${to ?? 'na'}|${box}`;
}

/**
 * @param {Record<string, unknown>[]} photos
 * @returns {Map<string, { wet: Record<string, unknown>[], dry: Record<string, unknown>[], unknown: Record<string, unknown>[], all: Record<string, unknown>[] }>}
 */
export function buildIntervalGroups(photos) {
  const map = new Map();

  for (const p of photos) {
    const key = intervalKeyFromPhoto(p);
    if (!map.has(key)) {
      map.set(key, { wet: [], dry: [], unknown: [], all: [] });
    }
    const g = map.get(key);
    g.all.push(p);
    const c = inferCondition(p);
    if (c === 'wet') g.wet.push(p);
    else if (c === 'dry') g.dry.push(p);
    else g.unknown.push(p);
  }
  return map;
}

/** @returns {typeof photos} */
export function sortPhotosForGallery(photos) {
  return [...photos].sort((a, b) => {
    const da = Number(a.depth_from_m);
    const db = Number(b.depth_from_m);
    if (Number.isFinite(da) && Number.isFinite(db) && da !== db) return da - db;
    if (Number.isFinite(da) && !Number.isFinite(db)) return -1;
    if (!Number.isFinite(da) && Number.isFinite(db)) return 1;

    const ta = Number(a.depth_to_m);
    const tb = Number(b.depth_to_m);
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;

    const ba = Number(a.box_start);
    const bb = Number(b.box_start);
    if (Number.isFinite(ba) && Number.isFinite(bb) && ba !== bb) return ba - bb;

    const fa = String(a.file_name || '');
    const fb = String(b.file_name || '');
    return fa.localeCompare(fb);
  });
}

/**
 * Ordered list of SAS URLs to try for a tile or <img onError fallback>.
 * Prefers thumb renditions, then the canonical original (image_asset.sas_url) — Often only the
 * original exists in storage while rendition paths in DB still point at missing thumbnails.
 */
export function enumerateThumbnailUrlCandidates(photo) {
  const out = [];
  const seen = new Set();
  const push = (u) => {
    if (!u || typeof u !== 'string' || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };

  const r = photo.renditions || {};
  if (typeof r === 'object' && !Array.isArray(r)) {
    for (const k of CORE_PHOTO_RENDITION_THUMB_KEYS) {
      const row = r[k];
      const u = row && typeof row.sas_url === 'string' ? row.sas_url : null;
      push(u);
    }
  }

  push(typeof photo.sas_url === 'string' ? photo.sas_url : null);

  if (typeof r === 'object' && !Array.isArray(r)) {
    const rest = [];
    for (const info of Object.values(r)) {
      if (!info || typeof info !== 'object') continue;
      const u = typeof info.sas_url === 'string' ? info.sas_url : null;
      if (!u) continue;
      const w = Number(info.width_px);
      const ww = Number.isFinite(w) && w > 0 ? w : 4096;
      rest.push({ u, ww });
    }
    rest.sort((a, b) => a.ww - b.ww);
    for (const { u } of rest) push(u);
  }

  return out;
}

/** Best URL for thumbnail grid — first candidate from {@link enumerateThumbnailUrlCandidates}. */
export function pickThumbnailUrl(photo) {
  const c = enumerateThumbnailUrlCandidates(photo);
  return c[0] ?? null;
}

/**
 * Prefer original; fall back to largest preview rendition with SAS.
 *
 * @param {Record<string, unknown>} photo
 */
export function pickFullSizeUrl(photo) {
  if (typeof photo.sas_url === 'string' && photo.sas_url) return photo.sas_url;
  const r = photo.renditions || {};
  for (const k of CORE_PHOTO_RENDITION_PREVIEW_KEYS) {
    const row = r[k];
    const u = row && typeof row.sas_url === 'string' ? row.sas_url : null;
    if (u) return u;
  }
  let bestUrl = null;
  let bestW = 0;
  for (const info of Object.values(r)) {
    if (!info || typeof info !== 'object') continue;
    const u = typeof info.sas_url === 'string' ? info.sas_url : null;
    if (!u) continue;
    const w = Number(info.width_px);
    const ww = Number.isFinite(w) && w > 0 ? w : 0;
    if (!bestUrl || ww > bestW) {
      bestW = ww;
      bestUrl = u;
    }
  }
  return bestUrl || pickThumbnailUrl(photo);
}

/**
 * URLs to try in the lightbox when the preferred full-size SAS fails (404 / auth).
 */
export function enumerateFullSizeUrlCandidates(photo) {
  const out = [];
  const seen = new Set();
  const push = (u) => {
    if (!u || typeof u !== 'string' || seen.has(u)) return;
    seen.add(u);
    out.push(u);
  };

  push(typeof photo.sas_url === 'string' ? photo.sas_url : null);

  const r = photo.renditions || {};
  if (typeof r === 'object' && !Array.isArray(r)) {
    for (const k of CORE_PHOTO_RENDITION_PREVIEW_KEYS) {
      const row = r[k];
      const u = row && typeof row.sas_url === 'string' ? row.sas_url : null;
      push(u);
    }

    const rest = [];
    for (const info of Object.values(r)) {
      if (!info || typeof info !== 'object') continue;
      const u = typeof info.sas_url === 'string' ? info.sas_url : null;
      if (!u) continue;
      const w = Number(info.width_px);
      const ww = Number.isFinite(w) && w > 0 ? w : 0;
      rest.push({ u, ww });
    }
    rest.sort((a, b) => b.ww - a.ww);
    for (const { u } of rest) push(u);
  }

  for (const u of enumerateThumbnailUrlCandidates(photo)) push(u);
  return out;
}
